import {combineReducers} from 'redux';
// @ts-expect-error
import {createReducer} from 'redux-act';

import type {ReportRowData, TimeseriesReportData} from 'nutshell-core/types';

import {activityReportReducer} from './activity/activity-report-reducer';
import type {ActivityReportState} from './activity/activity-report-reducer';
import {emailReportReducer} from './activity/email-report-reducer';
import type {EmailReportState} from './activity/email-report-reducer';
import {
    installmentsReportReducer,
    type InstallmentsReportState,
} from './installments/installments-report-reducer';
import {quotesReportReducer} from './quotes/quotes-report-reducer';
import type {QuotesReportState} from './quotes/quotes-report-reducer';
import {FORECAST, ACTIVITY, EMAIL, TASKS, QUOTES, INVOICES, CONTRACTS} from './report-constants';
import type {
    CustomLeadStats,
    ForecastStats,
    ActivityStats,
    EmailStats,
    TasksStats,
    QuotesStats,
} from './report-summary';
import type {LeadsReportKey} from './report-types';
import * as actions from './reports-actions';
import {taskReportReducer} from './tasks/task-report-reducer';
import type {TaskReportState} from './tasks/task-report-reducer';

type LeadsListState = {
    isLoading: boolean;
    isFirstLoad: boolean;
    errorMessage?: string;
    listItems: any[]; // TODO (@ianvs): define this type,
    meta: any; // TODO (@ianvs): define this type
};
const leadsListDefaultState = {
    isLoading: true,
    isFirstLoad: true,
    errorMessage: undefined,
    listItems: [],
    meta: {},
} as const;

const defaultStat = {value: 0, formatted: '0', prefix: '$', suffix: ''} as const;
const defaultCustomSummaryStats: CustomLeadStats = {
    value: {mean: defaultStat, sum: defaultStat},
    openedTime: {mean: defaultStat},
    winRate: {value: defaultStat},
};
const defaultForecastSummaryStats: ForecastStats = {
    sales: {total: defaultStat, count: defaultStat},
    projected: {total: defaultStat, count: defaultStat},
    pipeline: {total: defaultStat, count: defaultStat},
    quota: {total: defaultStat, count: defaultStat},
};
const defaultActivitySummaryStats: ActivityStats = {
    activities: {mean: defaultStat, sum: defaultStat},
    duration: {mean: defaultStat, sum: defaultStat},
};
const defaultEmailSummaryStats: EmailStats = {
    emails: {mean: defaultStat, sum: defaultStat},
};
const defaultTasksSummaryStats: TasksStats = {
    tasks: {
        total: defaultStat,
        completedOnTime: defaultStat,
        completedLate: defaultStat,
        onTimePercentage: defaultStat,
    },
};
const defaultQuotesSummaryStats: QuotesStats = {
    open: {count: defaultStat, value: defaultStat},
    overdue: {count: defaultStat, value: defaultStat},
};
const defaultContractsSummaryStats = {
    draft: {count: defaultStat},
    sent: {count: defaultStat},
    accepted: {count: defaultStat},
};

type SummaryStatsState = {
    isLoading: boolean;
    stats: CustomLeadStats | ForecastStats | ActivityStats | EmailStats;
};
const summaryStatsDefaultState = {
    isLoading: true,
    stats: defaultCustomSummaryStats,
} as const;

const isFilterDrawerOpen = createReducer(
    {
        // @ts-expect-error
        [actions.toggleShowFilterDrawer]: (state) => !state,
    },
    false
);

export const leadsListReducer = createReducer(
    {
        // @ts-expect-error
        [actions.requestReportLeadsListData]: (state) => {
            return {...state, isLoading: true, errorMessage: undefined};
        },
        // @ts-expect-error
        [actions.updateReportLeadsListData]: (state, {meta, listItems}) => {
            return {
                ...state,
                isLoading: false,
                isFirstLoad: false,
                meta: meta,
                listItems: listItems,
                errorMessage: undefined,
            };
        },
        // @ts-expect-error
        [actions.failReportLeadsListData]: (state, err) => {
            return {...state, isLoading: false, isFirstLoad: false, errorMessage: err};
        },
    },
    leadsListDefaultState
);

export const summaryStatsReducer = createReducer(
    {
        // @ts-expect-error
        [actions.updateReportLeadsListSummaryData]: (state, {meta}) => {
            return {...state, isLoading: false, stats: meta.stats};
        },
        // @ts-expect-error
        [actions.requestReportLeadsListData]: (state, {reportKey}) => {
            return reportKey !== FORECAST
                ? {...state, isLoading: true, stats: defaultCustomSummaryStats}
                : state;
        },
        // @ts-expect-error
        [actions.requestLeadsReportData]: (state, {reportKey}) => {
            if (reportKey === FORECAST) {
                return {...state, isLoading: true, stats: defaultForecastSummaryStats};
            } else if (reportKey === ACTIVITY) {
                return {...state, isLoading: true, stats: defaultActivitySummaryStats};
            } else if (reportKey === EMAIL) {
                return {...state, isLoading: true, stats: defaultEmailSummaryStats};
            } else if (reportKey === TASKS) {
                return {...state, isLoading: true, stats: defaultTasksSummaryStats};
            } else if (reportKey === QUOTES || reportKey === INVOICES) {
                return {...state, isLoading: true, stats: defaultQuotesSummaryStats};
            } else if (reportKey === CONTRACTS) {
                return {...state, isLoading: true, stats: defaultContractsSummaryStats};
            } else {
                return state;
            }
        },
        // @ts-expect-error
        [actions.updateForecastSummaryStats]: (state, stats) => {
            return {...state, isLoading: false, stats};
        },
        // @ts-expect-error
        [actions.updateActivitySummaryStats]: (state, {stats}) => {
            return {...state, isLoading: false, stats: stats};
        },
        // @ts-expect-error
        [actions.updateEmailSummaryStats]: (state, {stats}) => {
            return {...state, isLoading: false, stats: stats};
        },
        // @ts-expect-error
        [actions.updateAnalyticsSummaryStats]: (state, {stats}) => {
            return {...state, isLoading: false, stats: stats};
        },
        // @ts-expect-error
        [actions.updateTasksSummaryStats]: (state, {stats}) => {
            return {...state, isLoading: false, stats: stats};
        },
        // @ts-expect-error
        [actions.updateQuotesSummaryStats]: (state, {stats}) => {
            return {...state, isLoading: false, stats: stats};
        },
    },
    summaryStatsDefaultState
);

type LeadsReportTableData = {
    rows: Array<ReportRowData> | null | undefined;
    totals: ReportRowData | null | undefined;
};
type LeadsReportState = {
    tableData: LeadsReportTableData;
    reportKey: LeadsReportKey | null | undefined;
    timeseriesChart: {
        isLoading: boolean;
        isTimedOut: boolean;
        isErrored: boolean;
        chartData: TimeseriesReportData | null | undefined;
        prefix: string;
    };
};
const leadsReportDefaultState = {
    tableData: {rows: null, totals: null},
    reportKey: null,
    timeseriesChart: {
        isLoading: true,
        isTimedOut: false,
        isErrored: false,
        chartData: null,
        prefix: '$',
    },
} as const;

const leadsReport = createReducer(
    {
        // @ts-expect-error
        [actions.updateLeadsReportTableData]: (state, payload: LeadsReportTableData) => {
            return {...state, tableData: payload};
        },
        // @ts-expect-error
        [actions.requestLeadsReportData]: (state, {reportKey}) => {
            const newChartState = {
                ...state.timeseriesChart,
                isLoading: true,
                isTimedOut: false,
                isErrored: false,
            } as const;
            // If we are on a new report, delete the existing data right away
            if (reportKey !== state.reportKey) {
                newChartState.chartData = null;
            }

            return {...state, reportKey, timeseriesChart: newChartState};
        },
        // @ts-expect-error
        [actions.updateLeadsReportChartData]: (state, {chartData}) => {
            const newChartState = {
                ...state.timeseriesChart,
                isLoading: false,
                isTimedOut: false,
                isErrored: false,
                chartData,
            } as const;

            return {...state, timeseriesChart: newChartState};
        },
        // @ts-expect-error
        [actions.failLeadsReportChartData]: (state, err) => {
            if (err) {
                if (err.status === 504 || err.status === 502) {
                    const newChartState = {
                        ...state.timeseriesChart,
                        chartData: null,
                        isLoading: false,
                        isTimedOut: true,
                        isErrored: false,
                    } as const;

                    return {...state, timeseriesChart: newChartState};
                } else {
                    const newChartState = {
                        ...state.timeseriesChart,
                        chartData: null,
                        isLoading: false,
                        isTimedOut: false,
                        isErrored: true,
                    } as const;

                    return {...state, timeseriesChart: newChartState};
                }
            }

            return state;
        },
    },
    leadsReportDefaultState
);

// ── Products report state ───────────────────────────────────────────────────
export type ProductsReportRow = {
    id: string;
    name: string;
    sku: string;
    category: string;
    units: number;
    revenue: number;
    leads: number;
};
export type ProductsReportHeroSegment = {
    id: string | null;
    name: string;
    sku: string;
    category: string;
    units: number;
    revenue: number;
    leads: number;
    value: number;
    isOther: boolean;
    otherCount: number;
};
export type ProductsReportKpis = {
    uniqueProducts: number;
    totalUnits: number;
    totalLeads: number;
    totalRevenue: number;
};
export type ProductsReportState = {
    isLoading: boolean;
    isFirstLoad: boolean;
    errorMessage?: string;
    kpis: ProductsReportKpis;
    hero: {
        metric: 'revenue' | 'units';
        total: number;
        segments: ProductsReportHeroSegment[];
    };
    rows: ProductsReportRow[];
    totalProducts: number;
    page: number;
    limit: number;
    sort: string;
};
const productsReportDefaultState: ProductsReportState = {
    isLoading: true,
    isFirstLoad: true,
    errorMessage: undefined,
    kpis: {uniqueProducts: 0, totalUnits: 0, totalLeads: 0, totalRevenue: 0},
    hero: {metric: 'revenue', total: 0, segments: []},
    rows: [],
    totalProducts: 0,
    page: 1,
    limit: 100,
    sort: '-revenue',
};
const productsReport = createReducer(
    {
        // @ts-expect-error
        [actions.requestProductsReportData]: (state) => ({
            ...state,
            isLoading: true,
            errorMessage: undefined,
        }),
        // @ts-expect-error
        [actions.updateProductsReportData]: (state, response) => ({
            ...state,
            isLoading: false,
            isFirstLoad: false,
            errorMessage: undefined,
            kpis: response.kpis,
            hero: response.hero,
            rows: response.rows,
            totalProducts: response.totalProducts,
            page: response.page,
            limit: response.limit,
            sort: response.sort,
        }),
        // @ts-expect-error
        [actions.failProductsReportData]: (state, err) => ({
            ...state,
            isLoading: false,
            isFirstLoad: false,
            errorMessage: err && err.message ? err.message : 'Failed to load products report',
        }),
    },
    productsReportDefaultState
);

type ReportMapsState = {
    [id: string]: {
        id: string;
        name: string;
        key: string;
        prefix: string;
    };
};
const reportMaps = createReducer(
    {
        // @ts-expect-error
        [actions.updateReportMaps]: (state, payload) => payload,
    },
    {}
);

export const reportsReducer = combineReducers({
    activity: activityReportReducer,
    email: emailReportReducer,
    tasks: taskReportReducer,
    quotes: quotesReportReducer,
    installments: installmentsReportReducer,
    summaryStats: summaryStatsReducer,
    isFilterDrawerOpen,
    leadsList: leadsListReducer,
    leadsReport,
    productsReport,
    reportMaps,
});

export type ReportsState = {
    activity: ActivityReportState;
    email: EmailReportState;
    tasks: TaskReportState;
    quotes: QuotesReportState;
    installments: InstallmentsReportState;
    summaryStats: SummaryStatsState;
    isFilterDrawerOpen: boolean;
    leadsList: LeadsListState;
    leadsReport: LeadsReportState;
    productsReport: ProductsReportState;
    reportMaps: ReportMapsState;
};
